import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import bcrypt from 'bcryptjs';
import { supabase } from '../utils/supabaseClient';
import './EmailConfirmation.css';

/**
 * Landing page hit when a teacher / student clicks the confirmation link
 * Supabase emailed them after sign-up.
 *
 * Flow:
 *   1. verifyOtp({ token_hash }) — Supabase marks email_confirmed_at + sets
 *      a session (so the user is authenticated from this moment on).
 *   2. Read user.user_metadata to know the role.
 *   3. For teachers — redirect home; the teachers-table row was inserted at
 *      sign-up time (no RLS issue there, the insert used the auth user id).
 *   4. For students — pull the invite slug we stored in localStorage at
 *      sign-up, link the student record to this new auth user via the
 *      `update_student_on_registration` RPC (works now that auth.uid()
 *      matches), and redirect into their dashboard.
 */
export default function EmailConfirmation() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking'); // checking | success | error
  const [message, setMessage] = useState('Confirming your email...');

  useEffect(() => {
    const run = async () => {
      try {
        let user = null;

        // Supabase can land us here in three ways depending on the email
        // template / project config:
        //   A) ?token_hash=...&type=signup   → must call verifyOtp manually
        //   B) ?token=...&type=signup        → same (older format)
        //   C) #access_token=...&refresh_token=...  → Supabase already
        //      verified the OTP on its servers and put the tokens in the
        //      URL fragment. The JS client auto-detects this on load and
        //      establishes a session. We just have to read getSession()
        //      (with a tiny wait for the auto-detection to finish).
        const tokenHash = searchParams.get('token_hash') || searchParams.get('token');
        const type = searchParams.get('type') || 'signup';

        if (tokenHash) {
          const { data, error } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type });
          if (error) {
            console.error('verifyOtp error:', error);
            setStatus('error');
            setMessage(error.message || 'We could not confirm your email. The link may have expired.');
            return;
          }
          user = data?.user || null;
        } else {
          // Fragment flow — wait for the Supabase client to pick up the
          // tokens from window.location.hash and set a session.
          for (let i = 0; i < 20 && !user; i++) {
            const { data } = await supabase.auth.getSession();
            if (data?.session?.user) { user = data.session.user; break; }
            await new Promise(r => setTimeout(r, 100));
          }
        }

        if (!user) {
          setStatus('error');
          setMessage('Invalid or expired confirmation link. Please open the latest email or request a new one.');
          return;
        }

        // Register.jsx stores a pendingRegistration blob in localStorage at
        // sign-up time. We use it as a robust fallback for role/name when
        // user_metadata wasn't populated on this verifyOtp response (seen
        // intermittently across Supabase project configurations — when it
        // happens, a teacher gets misclassified as a student and the
        // teachers row never gets inserted, which later breaks any RPC
        // that joins on teachers.auth_user_id).
        const pendingReg = (() => {
          try { return JSON.parse(localStorage.getItem('pendingRegistration') || 'null'); }
          catch { return null; }
        })();
        const role = user.user_metadata?.role || pendingReg?.role || 'student';
        const name = user.user_metadata?.name || pendingReg?.name || '';
        // The slug from user_metadata is the source of truth; fall back to
        // localStorage in case the metadata wasn't set (older sign-ups).
        const inviteSlug = user.user_metadata?.student_invite_slug
          || pendingReg?.inviteSlug
          || (() => { try { return localStorage.getItem('pendingStudentInviteSlug'); } catch (_) { return null; } })();

        try {
          localStorage.setItem('currentUser', JSON.stringify({
            id: user.id, email: user.email, name, role
          }));
          localStorage.setItem('isAuthenticated', 'true');
        } catch (_) {}

        // ── INSERT TEACHER OR STUDENT ROW ────────────────────────────────
        // At this point a session is established (verifyOtp set it), so
        // auth.uid() is the new user — RLS policies that compare
        // auth_user_id / user_account_id against auth.uid() are satisfied.
        //
        // The legacy users/teachers tables predate the Supabase Auth
        // migration and still have a NOT NULL password_hash column.
        // We don't have the cleartext password here (Supabase already
        // hashed and stored it in auth.users), so write a placeholder
        // bcrypt that satisfies the constraint. Actual login goes
        // through Supabase Auth — this column is never used to
        // authenticate anyone again.
        // The legacy users table predates Supabase Auth and may still carry a
        // NOT NULL password_hash column. Real auth is delegated to Supabase
        // (auth.users.encrypted_password); this placeholder just satisfies
        // the schema constraint if it's still there.
        const passwordHash = await bcrypt.hash(`supabase-auth-${user.id}`, 10);
        if (role === 'teacher') {
          // Make sure the supabase client has actually picked up the
          // verifyOtp-issued session before the insert. Without this the
          // request can go out as the anonymous role, which an RLS policy
          // that compares auth.uid() to auth_user_id will reject.
          try { await supabase.auth.getSession(); } catch (_) {}

          let lastInsertError = null;
          try {
            // The teachers table dropped password_hash when auth moved to
            // Supabase Auth — including it now causes PGRST204
            // "column not found in schema cache".
            const { error: insertErr } = await supabase
              .from('teachers')
              .insert({
                auth_user_id: user.id,
                email: user.email,
                name,
                phone: null,
              });
            if (insertErr && insertErr.code !== '23505') {
              lastInsertError = insertErr;
              console.warn('teacher insert error:', insertErr.code, insertErr.message);
            }
          } catch (insErr) {
            lastInsertError = insErr;
            console.warn('teacher insert exception:', insErr?.message);
          }

          // Verify the row actually landed. Surface the real Supabase error
          // (code + message) so we can debug RLS / schema mismatches
          // without having to open the browser console.
          const { data: verifyRow, error: verifyErr } = await supabase
            .from('teachers')
            .select('id')
            .eq('auth_user_id', user.id)
            .maybeSingle();
          if (verifyErr) console.warn('teacher verify error:', verifyErr.code, verifyErr.message);
          if (!verifyRow) {
            const detail = lastInsertError
              ? `${lastInsertError.code || 'error'}: ${lastInsertError.message || lastInsertError}`
              : verifyErr
                ? `verify ${verifyErr.code || 'error'}: ${verifyErr.message}`
                : 'no row visible after insert (likely RLS)';
            setStatus('error');
            setMessage(
              'Your email was confirmed, but the teacher record could not be created. ' +
              'Database said: ' + detail + '. ' +
              'Email: ' + user.email
            );
            return;
          }
        } else if (role === 'student') {
          // Mirror the users table row (used elsewhere in the app)
          try {
            const { error: usersErr } = await supabase
              .from('users')
              .insert({
                id: user.id,
                email: user.email,
                name,
                role: 'student',
                password_hash: passwordHash,
              });
            if (usersErr && usersErr.code !== '23505') {
              console.warn('users insert error:', usersErr.message);
            }
          } catch (usrErr) {
            console.warn('users insert exception:', usrErr?.message);
          }
        }

        if (role === 'student' && inviteSlug) {
          // Now that we're authenticated, link the existing student row to
          // this auth user. This RPC bypasses RLS row-by-row to set
          // user_account_id + email + name in one shot.
          try {
            const { getStudentByInviteSlug } = await import('../utils/studentsService');
            const student = await getStudentByInviteSlug(inviteSlug);
            if (student?.id) {
              const { error: rpcError } = await supabase.rpc('update_student_on_registration', {
                p_student_id: student.id,
                p_user_account_id: user.id,
                p_email: user.email,
                p_name: name || student.name
              });
              if (rpcError) console.warn('Student link RPC failed:', rpcError.message);
            } else {
              console.warn('Student row not found for slug:', inviteSlug);
            }
          } catch (linkErr) {
            console.warn('Student linking error:', linkErr?.message);
          }
        }

        try { localStorage.removeItem('pendingStudentInviteSlug'); } catch (_) {}
        try { localStorage.removeItem('pendingRegistrationEmail'); } catch (_) {}

        setStatus('success');
        setMessage(role === 'student' ? 'Welcome — opening your lessons…' : 'Welcome — opening your dashboard…');

        setTimeout(() => {
          if (role === 'student' && inviteSlug) {
            window.location.href = `/student-dashboard/${inviteSlug}`;
          } else {
            window.location.href = '/';
          }
        }, 1200);
      } catch (e) {
        console.error('EmailConfirmation unexpected error:', e);
        setStatus('error');
        setMessage('Something went wrong while confirming your email. Please try again.');
      }
    };
    run();
  }, [searchParams, navigate]);

  return (
    <div className="email-confirmation-container">
      <div className="email-confirmation-card">
        {status === 'checking' && (
          <>
            <div className="spinner"></div>
            <h2>Confirming your email…</h2>
            <p>{message}</p>
          </>
        )}
        {status === 'success' && (
          <>
            <div className="success-icon">✓</div>
            <h2>Email confirmed</h2>
            <p>{message}</p>
          </>
        )}
        {status === 'error' && (
          <>
            <div className="error-icon">✗</div>
            <h2>Confirmation failed</h2>
            <p>{message}</p>
            <button className="retry-button" onClick={() => navigate('/login')}>
              Go to Login
            </button>
          </>
        )}
      </div>
    </div>
  );
}
